/**
 * Community Helpers - Utility functions for public community pages
 */

// Gradient color options for cover images
const gradients = [
  ['#667eea', '#764ba2'], // Blue to purple
  ['#f093fb', '#f5576c'], // Pink to coral  
  ['#4facfe', '#00f2fe'], // Blue to cyan
  ['#43e97b', '#38f9d7'], // Green to teal
  ['#fa709a', '#fee140'], // Pink to yellow
  ['#a8edea', '#fed6e3'], // Mint to pink
  ['#ffecd2', '#fcb69f'], // Peach to coral
];

/**
 * Get gradient colors based on community name hash
 */
export function getGradientColors(communityName) {
  const hash = communityName.split('').reduce((a, b) => {
    a = ((a << 5) - a) + b.charCodeAt(0);
    return a & a;
  }, 0);
  return gradients[Math.abs(hash) % gradients.length];
}

/**
 * Tag colors for posts
 */
export const tagColors = {
  'Announcement': { bg: '#FFE5E5', text: '#FF6B6B' },
  'Mazal Tov': { bg: '#E5FFE5', text: '#4CAF50' },
  'Update': { bg: '#E5F5FF', text: '#2196F3' },
  'News': { bg: '#FFF5E5', text: '#FF9800' },
  'Chesed': { bg: '#F5E5FF', text: '#9C27B0' },
  'Tehilim': { bg: '#E5FFFF', text: '#00BCD4' },
  default: { bg: '#F0F0F0', text: '#666' }
};

/**
 * Get tag color configuration
 */
export function getTagColor(tag) {
  return tagColors[tag] || tagColors.default;
}

/**
 * Get gradient colors for tag backgrounds (matching native app)
 */
export function getTagGradient(tag) {
  const gradientMap = {
    'Announcement': ['#667eea', '#764ba2'], // Blue to Purple
    'Mazal Tov': ['#56ab2f', '#a8e6cf'], // Green to Light Green
    'Update': ['#4facfe', '#00f2fe'], // Light Blue to Cyan
    'News': ['#ff9a9e', '#fecfef'], // Pink to Light Pink
    'Chesed': ['#a8edea', '#fed6e3'], // Mint to Light Pink
    'Tehilim': ['#ffecd2', '#fcb69f'], // Light Yellow to Peach
    'Event': ['#43e97b', '#38f9d7'], // Green to Turquoise
    'Prayer Request': ['#fa709a', '#fee140'], // Pink to Yellow
    'Community': ['#a8edea', '#fed6e3'], // Mint to Light Pink
    'Torah': ['#ffecd2', '#fcb69f'], // Light Yellow to Peach
    default: ['#e0c3fc', '#9bb5ff'] // Light Purple to Blue
  };
  return gradientMap[tag] || gradientMap.default;
}

/**
 * Get emoji for tag
 */
export function getTagEmoji(tag) {
  const emojiMap = {
    'Announcement': '📢',
    'Mazal Tov': '🎉',
    'Update': '🔄',
    'News': '📰',
    'Chesed': '🤝',
    'Tehilim': '🕊️',
    'Event': '🎪',
    'Prayer Request': '🙏',
    'Community': '👥',
    'Torah': '📜',
    default: '💬'
  };
  return emojiMap[tag] || emojiMap.default;
}

/**
 * Format price for events
 */
export function getPriceText(event) {
  if (!event.pricing_type || event.pricing_type === 'free') return 'Free';
  
  if (event.pricing_type === 'flat') {
    if (event.ticket_price_cents) {
      return `$${(event.ticket_price_cents / 100).toFixed(2)}`;
    }
    return 'Free';
  }
  
  if (event.pricing_type === 'tiered' && event.pricing_rules?.length > 0) {
    const prices = event.pricing_rules.map(r => r.price_cents || r.priceCents).filter(p => p >= 0);
    if (prices.length > 0) {
      const min = Math.min(...prices);
      const max = Math.max(...prices);
      if (min === max) return `$${(min / 100).toFixed(2)}`;
      return `$${(min / 100).toFixed(2)} - $${(max / 100).toFixed(2)}`;
    }
  }
  
  return 'Free';
}

/**
 * Calculate Stripe fee
 */
export function calculateFee(amount) {
  return (amount * 0.032) + 0.40; // 3.2% + $0.40
}

/**
 * Get total amount with optional fee coverage
 */
export function getTotalAmount(amount, coverFee) {
  const fee = coverFee ? calculateFee(amount) : 0;
  return amount + fee;
}

/**
 * Format time from date string
 */
export function formatTime(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

/**
 * Format date
 */
export function formatDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}

/**
 * Format long date
 */
export function formatLongDate(date) {
  return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}

/**
 * Format class date and time
 */
export function formatClassDateTime(classItem) {
  // Recurring classes
  if (classItem.is_recurring) {
    const time = formatTime(classItem.date);
    
    if (classItem.recurrence_pattern === 'daily') {
      return time ? `Every day at ${time}` : 'Every day';
    }
    
    if (classItem.recurrence_pattern === 'weekly' && classItem.recurrence_day) {
      return time ? `Every ${classItem.recurrence_day} at ${time}` : `Every ${classItem.recurrence_day}`;
    }
    
    return classItem.recurrence_pattern || 'Recurring';
  }
  
  // Non-recurring classes
  if (classItem.date) {
    const date = new Date(classItem.date);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000);
    const classDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    if (classDate.getTime() === today.getTime()) {
      return `Today, ${formatDate(date)} ${formatTime(date)}`;
    }
    if (classDate.getTime() === tomorrow.getTime()) {
      return `Tomorrow, ${formatDate(date)} ${formatTime(date)}`;
    }
    return `${formatLongDate(date)} ${formatTime(date)}`;
  }
  
  return classItem.schedule || 'Ongoing';
}

/**
 * Check if location is a Zoom link
 */
export function isZoomLink(location) {
  if (!location) return false;
  const lower = location.toLowerCase();
  return lower.includes('zoom') || 
         lower.includes('zoom.us') || 
         lower.includes('us02web.zoom.us');
}

/**
 * Format time ago
 */
export function timeAgo(dateString) {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);
  
  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return formatDate(dateString);
}

/**
 * Get day abbreviation
 */
export function getDayAbbrev(dayIndex) {
  const abbrevs = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
  return abbrevs[dayIndex];
}

/**
 * Get day name
 */
export function getDayName(dayIndex) {
  const names = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return names[dayIndex];
}
